<template>
  <div class="contract-section-builder">
    <header class="builder-command-bar">
      <div class="builder-command-copy">
        <h6 class="mb-1">{{ $t('contract_builder') }}</h6>
        <small>{{ $t('practical_builder_help') }}</small>
      </div>
      <div class="view-mode-switcher" role="group" :aria-label="$t('builder_mode')">
        <button
          v-for="mode in viewModes"
          :key="mode.key"
          type="button"
          class="view-mode-button"
          :class="{ active: viewMode === mode.key }"
          :aria-pressed="viewMode === mode.key ? 'true' : 'false'"
          @click="setViewMode(mode.key)"
        >
          <AppIcon :name="mode.icon" class="bi" :class="mode.icon" />
          <span>{{ mode.label }}</span>
        </button>
      </div>
    </header>

    <div class="builder-workspace" :class="{ 'single-column': viewMode !== 'visual' }">
      <div class="editor-column">
        <template v-if="viewMode === 'visual'">
        <div ref="quillToolbarDock" class="quill-toolbar-dock"></div>

        <div class="unified-document-canvas">
          <article
            class="unified-document-page"
            :class="{ 'has-focused-block': !!activeBlockId }"
            :style="documentPageStyle"
          >
            <section
              v-for="section in visibleSectionOptions"
              :key="section.key"
              class="document-section-shell"
              :class="[`document-section-shell-${section.key}`, { active: activeSection === section.key, empty: !sectionHasContent(section.key) }]"
              @click.self="activateSection(section.key)"
            >
              <div class="section-label">
                <AppIcon :name="section.icon" class="bi" :class="section.icon" />{{ section.label }}
              </div>

              <div class="document-block-list">
                <div
                  v-for="block in sections[section.key]"
                  :key="block.id"
                  class="document-block-shell"
                  :class="{
                    active: activeBlockId === block.id,
                    'focus-muted': activeBlockId && activeBlockId !== block.id,
                    compact: isUtilityBlock(block)
                  }"
                  :style="blockContainerStyle(block)"
                  :aria-selected="activeBlockId === block.id ? 'true' : 'false'"
                  @click.stop="selectBlock(section.key, block.id)"
                >
                  <div v-if="block.type === 'divider'" class="utility-divider" :style="dividerStyle(block)"></div>
                  <div v-else-if="block.type === 'spacer'" class="utility-spacer" :style="{ height: `${block.styles.height || 32}px` }">
                    <span>{{ block.styles.height || 32 }}px</span>
                  </div>
                  <div
                    v-else-if="requiresStructuredEditor(block)"
                    class="structured-html-editor"
                    contenteditable="true"
                    role="textbox"
                    :aria-label="blockTypeLabel(block.type)"
                    :data-structured-block-id="block.id"
                    v-html="block.html"
                    @focus="selectBlock(section.key, block.id)"
                    @blur="updateStructuredBlock(block.id, $event)"
                  ></div>
                  <ContractEditor
                    v-else
                    :ref="`contractBlock-${block.id}`"
                    :value="block.html"
                    :variables="allowSmartVariables ? variables : []"
                    :editorMinHeight="blockEditorMinHeight(block)"
                    embedded
                    @focus="selectBlock(section.key, block.id)"
                    @input="updateBlock(block.id, $event)"
                  />
                </div>
              </div>

              <div
                class="section-add-control"
                :class="{ open: addMenuSection === section.key, 'in-flow': section.key === 'body' }"
              >
                <button
                  type="button"
                  class="section-add-button"
                  aria-haspopup="menu"
                  :aria-expanded="addMenuSection === section.key ? 'true' : 'false'"
                  @click.stop="toggleAddMenu(section.key)"
                >
                  <AppIcon name="bi-plus-lg" class="bi bi-plus-lg" />
                  <span>{{ $t('add_content') }}</span>
                  <AppIcon name="bi-chevron-down" class="bi bi-chevron-down" />
                </button>
                <div v-if="addMenuSection === section.key" class="section-add-menu" role="menu" @click.stop>
                  <button
                    v-for="blockType in blockTypes"
                    :key="blockType.key"
                    type="button"
                    class="section-add-option"
                    role="menuitem"
                    @click="addBlockToSection(blockType.key, section.key)"
                  >
                    <AppIcon :name="blockType.icon" class="bi" :class="blockType.icon" />
                    <span>{{ blockType.label }}</span>
                  </button>
                </div>
              </div>
            </section>
          </article>
        </div>
        </template>

        <div v-else-if="viewMode === 'preview'" class="preview-workspace">
          <div class="contract-preview-canvas">
            <article class="contract-preview-page" :style="documentPageStyle" v-html="previewHtml"></article>
          </div>
        </div>

        <div v-else class="developer-workspace">
          <div class="developer-header">
            <div>
              <h6 class="mb-1"><AppIcon name="bi-braces" class="bi bi-braces me-2" />{{ $t('json_template_editor') }}</h6>
              <small>{{ $t('json_editor_help') }}</small>
            </div>
            <div class="developer-actions">
              <button type="button" class="btn btn-sm btn-outline-light" :title="$t('format')" :aria-label="$t('format')" @click="formatJson">
                <AppIcon name="bi-code-square" class="bi bi-code-square" />
              </button>
              <button type="button" class="btn btn-sm btn-outline-light" :title="$t('copy')" :aria-label="$t('copy')" @click="copyJson">
                <AppIcon name="bi-clipboard" class="bi bi-clipboard" />
              </button>
              <button type="button" class="btn btn-sm btn-light" :title="$t('apply_json')" :aria-label="$t('apply_json')" @click="applyJsonTemplate">
                <AppIcon name="bi-check2-circle" class="bi bi-check2-circle" />
              </button>
            </div>
          </div>
          <div v-if="jsonError" class="json-error"><AppIcon name="bi-exclamation-triangle" class="bi bi-exclamation-triangle" />{{ jsonError }}</div>
          <textarea
            v-model="jsonTemplate"
            spellcheck="false"
            class="json-editor developer-editor-simple"
            :aria-label="$t('json_template_editor')"
            @input="jsonDirty = true; jsonError = ''"
          ></textarea>
        </div>
      </div>

      <aside v-if="viewMode === 'visual'" class="builder-sidebar simple-config-sidebar">
        <slot name="sidebar-top"></slot>

        <div class="sidebar-block">
          <div class="sidebar-heading mb-2">
            <span class="sidebar-icon"><AppIcon name="bi-layout-text-window" class="bi bi-layout-text-window" /></span>
            <div>
              <h6 class="small fw-bold mb-0">{{ $t('starter_layouts') }}</h6>
              <small class="sidebar-hint mt-0">{{ $t('starter_layouts_help') }}</small>
            </div>
          </div>
          <div class="starter-layout-list">
            <button
              v-for="preset in starterLayouts"
              :key="preset.key"
              type="button"
              class="starter-layout-button"
              :class="{ active: activePresetKey === preset.key }"
              @click="applyPreset(preset.key)"
            >
              <AppIcon :name="preset.icon" class="bi" :class="preset.icon" />
              <span>{{ preset.label }}</span>
              <AppIcon v-if="activePresetKey === preset.key" name="bi-check-lg" class="bi bi-check-lg ms-auto" />
            </button>
          </div>
        </div>

        <div class="sidebar-block">
          <div class="sidebar-heading">
            <span class="sidebar-icon"><AppIcon name="bi-plus-lg" class="bi bi-plus-lg" /></span>
            <div class="min-width-0">
              <h6 class="small fw-bold mb-0">{{ $t('quick_add') }}</h6>
              <small class="sidebar-hint mt-0">{{ $t('add_to_section', { section: activeSectionLabel }) }}</small>
            </div>
          </div>

          <div class="section-target-switcher" role="group" :aria-label="$t('document_sections')">
            <button
              v-for="section in sectionOptions"
              :key="section.key"
              type="button"
              :class="{ active: activeSection === section.key }"
              @click="activateSection(section.key)"
            >
              {{ section.label }}
            </button>
          </div>

          <div class="quick-block-grid">
            <button
              v-for="blockType in blockTypes"
              :key="blockType.key"
              type="button"
              class="quick-block-button"
              @click="addBlockToSection(blockType.key, activeSection)"
            >
              <AppIcon :name="blockType.icon" class="bi" :class="blockType.icon" />
              <span>{{ blockType.label }}</span>
            </button>
          </div>
        </div>

        <div class="sidebar-block selected-block-panel" :class="{ empty: !activeBlock }">
          <div class="sidebar-heading">
            <span class="sidebar-icon"><AppIcon name="bi-cursor" class="bi bi-cursor" /></span>
            <div>
              <h6 class="small fw-bold mb-0">{{ $t('selected_block') }}</h6>
              <small v-if="activeBlock">{{ blockTypeLabel(activeBlock.type) }} · {{ activeSectionLabel }}</small>
              <small v-else>{{ $t('select_block_help') }}</small>
            </div>
          </div>

          <template v-if="activeBlock">
            <div class="block-action-grid">
              <button type="button" class="block-action-button" :disabled="!canMoveActiveBlockUp" :title="$t('move_up')" @click="moveActiveBlock(-1)">
                <AppIcon name="bi-arrow-up" class="bi bi-arrow-up" />
                <span>{{ $t('move_up') }}</span>
              </button>
              <button type="button" class="block-action-button" :disabled="!canMoveActiveBlockDown" :title="$t('move_down')" @click="moveActiveBlock(1)">
                <AppIcon name="bi-arrow-down" class="bi bi-arrow-down" />
                <span>{{ $t('move_down') }}</span>
              </button>
              <button type="button" class="block-action-button" :title="$t('duplicate')" @click="duplicateActiveBlock">
                <AppIcon name="bi-files" class="bi bi-files" />
                <span>{{ $t('duplicate') }}</span>
              </button>
              <button type="button" class="block-action-button danger" :title="$t('delete')" @click="deleteActiveBlock">
                <AppIcon name="bi-trash" class="bi bi-trash" />
                <span>{{ $t('delete') }}</span>
              </button>
            </div>

            <div class="block-spacing-grid">
              <label>
                <span>{{ $t('padding') }}</span>
                <div class="input-group input-group-sm">
                  <input v-model.number="activeBlock.styles.padding" type="number" min="0" max="48" class="form-control" @change="normalizeActiveBlockStyle('padding', 0, 48, 0)">
                  <span class="input-group-text">px</span>
                </div>
              </label>
              <label>
                <span>{{ $t('space_after') }}</span>
                <div class="input-group input-group-sm">
                  <input v-model.number="activeBlock.styles.marginBottom" type="number" min="0" max="64" class="form-control" @change="normalizeActiveBlockStyle('marginBottom', 0, 64, 16)">
                  <span class="input-group-text">px</span>
                </div>
              </label>
            </div>
          </template>
        </div>

        <div v-if="viewMode === 'visual' && allowSmartVariables" class="sidebar-block">
          <div class="sidebar-heading">
            <span class="sidebar-icon text-primary"><AppIcon name="bi-braces" class="bi bi-braces" /></span>
            <h6 class="small fw-bold mb-0">{{ $t('smart_variables') }}</h6>
          </div>
          <select v-model="selectedVariableKey" class="form-select form-select-sm" @change="onVariableSelect">
            <option value="">{{ $t('insert_smart_field') }}</option>
            <optgroup v-for="group in variableGroups" :key="group.key" :label="group.label">
              <option v-for="variable in group.variables" :key="variable.key" :value="variable.key">
                {{ variableLabel(variable) }}
              </option>
            </optgroup>
          </select>
          <small class="sidebar-hint">{{ $t('select_block_to_insert_field') }}</small>
        </div>

        <details class="sidebar-block appearance-panel">
          <summary class="sidebar-summary">
            <span><AppIcon name="bi-palette" class="bi bi-palette me-2" />{{ $t('document_appearance') }}</span>
            <AppIcon name="bi-chevron-down" class="bi bi-chevron-down config-chevron" />
          </summary>
          <div class="appearance-fields">
            <label class="appearance-field appearance-field-wide">
              <span>{{ $t('font') }}</span>
              <select v-model="settings.fontFamily" class="form-select form-select-sm" @change="markChanged">
                <option v-for="font in fontOptions" :key="font.value" :value="font.value">{{ font.label }}</option>
              </select>
            </label>
            <div class="appearance-grid">
              <label class="appearance-field">
                <span>{{ $t('font_size') }}</span>
                <div class="input-group input-group-sm">
                  <input v-model.number="settings.fontSize" type="number" min="10" max="20" class="form-control" @change="normalizeDocumentFontSize">
                  <span class="input-group-text">px</span>
                </div>
              </label>
              <label class="appearance-field">
                <span>{{ $t('page_padding') }}</span>
                <div class="input-group input-group-sm">
                  <input :value="pagePadding" type="number" min="0" max="64" class="form-control" @change="updatePageSpacing('pagePadding', $event)">
                  <span class="input-group-text">px</span>
                </div>
              </label>
            </div>
            <div class="appearance-color-grid">
              <label>
                <span>{{ $t('text_color') }}</span>
                <input :value="colorInputValue(settings.textColor, '#1f2937')" type="color" class="form-control form-control-color" @input="updateDocumentColor('textColor', $event)">
              </label>
              <label>
                <span>{{ $t('accent_color') }}</span>
                <input :value="colorInputValue(settings.accentColor, '#303030')" type="color" class="form-control form-control-color" @input="updateDocumentColor('accentColor', $event)">
              </label>
            </div>
          </div>
        </details>

      </aside>
    </div>
  </div>
</template>

<script>
import ContractEditor from './ContractEditor.vue'
import defaultDocumentTemplates from './defaultTemplates'

const SCHEMA_NAME = 'gstocky.contract-template.v2'
const SECTION_KEYS = ['header', 'body', 'footer']
const DOCUMENT_TYPES = ['contract', 'certificate', 'attestation', 'letter', 'custom']
const BLOCK_TYPES = ['heading', 'text', 'clause', 'parties', 'signature', 'image', 'divider', 'spacer']

export default {
  name: 'ContractSectionBuilder',
  components: {
    ContractEditor
  },
  props: {
    value: { type: String, default: '' },
    variables: { type: Array, default: () => [] },
    variableValues: { type: Object, default: () => ({}) },
    previewName: { type: String, default: '' },
    pagePadding: { type: [Number, String], default: 20 },
    pageMargin: { type: [Number, String], default: 20 },
    documentType: { type: String, default: '' },
    allowSmartVariables: { type: Boolean, default: false }
  },
  data() {
    return {
      activeSection: 'body',
      activeBlockId: '',
      viewMode: 'visual',
      sections: { header: [], body: [], footer: [] },
      settings: this.defaultSettings(),
      lastEmittedValue: '',
      jsonTemplate: '',
      jsonError: '',
      jsonDirty: false,
      blockCounter: 0,
      activePresetKey: '',
      addMenuSection: '',
      selectedVariableKey: ''
    }
  },
  computed: {
    viewModes() {
      const modes = [
        { key: 'visual', label: this.$t('edit'), icon: 'bi-pencil-square' },
        { key: 'preview', label: this.$t('preview'), icon: 'bi-eye' }
      ]
      if (this.allowSmartVariables) {
        modes.push({ key: 'developer', label: this.$t('developer_mode'), icon: 'bi-braces' })
      }
      return modes
    },
    starterLayouts() {
      const matchingLayouts = defaultDocumentTemplates.filter(template => template.documentType === this.settings.documentType)
      const layouts = matchingLayouts.length
        ? matchingLayouts
        : defaultDocumentTemplates.filter(template => template.documentType === 'contract')
      return layouts.map(template => ({
        key: template.key,
        label: this.$t(template.nameKey),
        icon: template.icon || 'bi-file-earmark-text'
      }))
    },
    blockTypes() {
      return [
        { key: 'heading', label: this.$t('heading'), icon: 'bi-type-h1' },
        { key: 'text', label: this.$t('text'), icon: 'bi-text-paragraph' },
        { key: 'clause', label: this.$t('clause'), icon: 'bi-list-ol' },
        { key: 'parties', label: this.$t('party_details'), icon: 'bi-people' },
        { key: 'signature', label: this.$t('signatures'), icon: 'bi-pen' },
        { key: 'image', label: this.$t('image'), icon: 'bi-image' },
        { key: 'divider', label: this.$t('divider'), icon: 'bi-dash-lg' },
        { key: 'spacer', label: this.$t('spacer'), icon: 'bi-arrows-expand' }
      ]
    },
    fontOptions() {
      return [
        { label: 'Times New Roman', value: 'Times New Roman, Times, serif' },
        { label: 'Arial', value: 'Arial, Helvetica, sans-serif' },
        { label: 'Georgia', value: 'Georgia, serif' },
        { label: 'Verdana', value: 'Verdana, sans-serif' },
        { label: 'Courier New', value: 'Courier New, monospace' }
      ]
    },
    sectionOptions() {
      return [
        { key: 'header', label: this.$t('contract_header'), icon: 'bi-window' },
        { key: 'body', label: this.$t('contract_body'), icon: 'bi-file-text' },
        { key: 'footer', label: this.$t('contract_footer'), icon: 'bi-layout-text-sidebar-reverse' }
      ]
    },
    visibleSectionOptions() {
      return this.sectionOptions
    },
    activeSectionLabel() {
      const section = this.sectionOptions.find(item => item.key === this.activeSection)
      return section ? section.label : this.activeSection
    },
    activeBlock() {
      for (const sectionKey of SECTION_KEYS) {
        const block = this.sections[sectionKey].find(item => item.id === this.activeBlockId)
        if (block) return block
      }
      return null
    },
    activeBlockLocation() {
      return this.activeBlockId ? this.findBlockLocation(this.activeBlockId) : null
    },
    canMoveActiveBlockUp() {
      return Boolean(this.activeBlockLocation && this.activeBlockLocation.index > 0)
    },
    canMoveActiveBlockDown() {
      if (!this.activeBlockLocation) return false
      return this.activeBlockLocation.index < this.sections[this.activeBlockLocation.section].length - 1
    },
    variableGroups() {
      const groups = [
        { key: 'entity', label: this.$t('client_supplier'), variables: [] },
        { key: 'employee', label: this.$t('employee'), variables: [] },
        { key: 'contract', label: this.$t('contract'), variables: [] },
        { key: 'business', label: this.$t('your_business'), variables: [] },
        { key: 'other', label: this.$t('other'), variables: [] }
      ]

      this.variables.forEach(variable => {
        let groupKey = 'other'
        if (/\{\{\s*(entity|client|customer|supplier)\./.test(variable.key)) groupKey = 'entity'
        else if (/\{\{\s*employee\./.test(variable.key)) groupKey = 'employee'
        else if (/\{\{\s*contract\./.test(variable.key)) groupKey = 'contract'
        else if (/\{\{\s*(business|company)\./.test(variable.key)) groupKey = 'business'
        groups.find(group => group.key === groupKey).variables.push(variable)
      })

      return groups.filter(group => group.variables.length)
    },
    previewHtml() {
      let html = this.serializeSections()
      html = html.replace(/\{\{business\.logo\}\}/g, this.escapeHtml((this.business && this.business.logo) || ''))
      html = this.resolveVariableValues(html)

      return html.replace(/\{\{\s*([^}]+?)\s*\}\}/g, match => {
        const variable = this.variables.find(item => this.normalizeVariable(item.key) === this.normalizeVariable(match))
        const label = variable ? this.variableLabel(variable) : match
        return `<span class="unresolved-variable">${this.escapeHtml(label)}</span>`
      })
    },
    documentPageStyle() {
      return {
        padding: `${Number(this.pagePadding)}px`,
        margin: `${Number(this.pageMargin)}px auto`,
        color: this.settings.textColor,
        backgroundColor: this.settings.backgroundColor,
        fontFamily: this.settings.fontFamily,
        fontSize: `${this.settings.fontSize}px`,
        '--contract-accent': this.settings.accentColor
      }
    },
    business() {
      return this.$store && this.$store.getters ? (this.$store.getters.BUSINESS || {}) : {}
    }
  },
  watch: {
    value(value) {
      if (value === this.lastEmittedValue) return
      this.hydrateSections(value)
    },
    activeBlockId() {
      this.releaseToolbarDock()
      this.queueToolbarDockSync()
    },
    viewMode(value) {
      if (value !== 'visual') this.releaseToolbarDock()
      else this.queueToolbarDockSync()
    },
    documentType(value) {
      if (!value || !DOCUMENT_TYPES.includes(value) || value === this.settings.documentType) return
      this.settings.documentType = value
      this.markChanged()
    },
    pagePadding() {
      if (!this.jsonDirty) this.refreshJsonTemplate()
    },
    pageMargin() {
      if (!this.jsonDirty) this.refreshJsonTemplate()
    }
  },
  mounted() {
    this.hydrateSections(this.value)
    document.addEventListener('click', this.closeAddMenu)
  },
  beforeDestroy() {
    document.removeEventListener('click', this.closeAddMenu)
    this.releaseToolbarDock()
  },
  methods: {
    defaultSettings() {
      return {
        documentType: DOCUMENT_TYPES.includes(this.documentType) ? this.documentType : 'contract',
        fontFamily: 'Times New Roman, Times, serif',
        fontSize: 14,
        textColor: '#1f2937',
        accentColor: '#303030',
        backgroundColor: '#ffffff'
      }
    },
    defaultBlockStyles(type = 'text') {
      return {
        padding: type === 'heading' ? 8 : 0,
        marginBottom: type === 'spacer' ? 0 : 16,
        backgroundColor: '#ffffff',
        borderColor: '#d8dee9',
        borderWidth: type === 'divider' ? 1 : 0,
        borderRadius: 0,
        height: type === 'spacer' ? 32 : 0
      }
    },
    normalizeActiveBlockStyle(field, minimum, maximum, fallback) {
      if (!this.activeBlock || !this.activeBlock.styles) return
      this.activeBlock.styles[field] = this.numberInRange(
        this.activeBlock.styles[field],
        minimum,
        maximum,
        fallback
      )
      this.emitContent()
    },
    normalizeDocumentFontSize() {
      this.settings.fontSize = this.numberInRange(this.settings.fontSize, 10, 20, 14)
      this.emitContent()
    },
    updatePageSpacing(field, event) {
      const ranges = {
        pagePadding: { minimum: 0, maximum: 64, fallback: 20 },
        pageMargin: { minimum: 0, maximum: 48, fallback: 20 }
      }
      const range = ranges[field]
      if (!range) return
      const value = this.numberInRange(event && event.target ? event.target.value : '', range.minimum, range.maximum, range.fallback)
      this.$emit(`update:${field}`, value)
    },
    updateDocumentColor(field, event) {
      if (!['textColor', 'accentColor'].includes(field) || !event || !event.target) return
      this.settings[field] = this.safeColor(event.target.value, this.settings[field])
      this.emitContent()
    },
    colorInputValue(value, fallback) {
      const color = String(value || '').trim()
      const hexMatch = color.match(/^#([0-9a-f]{6})/i)
      if (hexMatch) return `#${hexMatch[1]}`
      const rgbMatch = color.match(/^rgba?\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)/i)
      if (!rgbMatch) return fallback
      return `#${rgbMatch.slice(1, 4).map(channel => Number(channel).toString(16).padStart(2, '0')).join('')}`
    },
    createBlock(type, html = null, styles = {}) {
      const normalizedType = BLOCK_TYPES.includes(type) ? type : 'text'
      return {
        id: this.createBlockId(),
        type: normalizedType,
        html: this.resolveEditorAssets(html === null ? this.defaultBlockHtml(normalizedType) : this.sanitizeHtml(html)),
        styles: { ...this.defaultBlockStyles(normalizedType), ...this.normalizeBlockStyles(styles, normalizedType) }
      }
    },
    createBlockId() {
      this.blockCounter += 1
      return `block-${Date.now().toString(36)}-${this.blockCounter.toString(36)}`
    },
    defaultBlockHtml(type) {
      const recipientName = this.preferredRecipientVariable('name')
      const content = {
        heading: `<h1 style="text-align: center;">${this.$t('document_title')}</h1>`,
        text: `<p>${this.$t('start_writing_here')}</p>`,
        clause: `<h2>${this.$t('clause_title')}</h2><p>${this.$t('clause_placeholder')}</p>`,
        parties: `<p><strong>${this.$t('between')}:</strong> {{business.name}}</p><p><strong>${this.$t('and')}:</strong> ${recipientName}</p>`,
        signature: `<p><strong>{{business.name}}</strong><br>${this.$t('signature')}: ____________________</p><p><strong>${recipientName}</strong><br>${this.$t('signature')}: ____________________</p>`,
        image: '<p style="text-align: center;"><img src="{{business.logo}}" alt="Logo" style="max-width: 180px;"></p>',
        divider: '',
        spacer: ''
      }
      const defaultHtml = content[type] || content.text
      return this.allowSmartVariables ? defaultHtml : this.resolveVariableValues(defaultHtml, true)
    },
    blockTypeLabel(type) {
      const item = this.blockTypes.find(blockType => blockType.key === type)
      return item ? item.label : type
    },
    blockEditorMinHeight(block) {
      if (block.type === 'heading' || block.type === 'image') return 70
      if (block.type === 'signature' || block.type === 'parties') return 120
      return 100
    },
    requiresStructuredEditor(block) {
      if (!block || !block.html) return false
      // Quill only preserves its own document model. Tables and layout containers
      // are valid contract HTML, but Quill silently removes their contents.
      return /<(?:div|table|thead|tbody|tfoot|tr|th|td|caption|iframe)\b/i.test(block.html)
    },
    isUtilityBlock(block) {
      return block.type === 'divider' || block.type === 'spacer'
    },
    blockContainerStyle(block) {
      const styles = block.styles || this.defaultBlockStyles(block.type)
      return {
        padding: `${this.numberInRange(styles.padding, 0, 48, 0)}px`,
        marginBottom: `${this.numberInRange(styles.marginBottom, 0, 64, 0)}px`,
        backgroundColor: this.safeColor(styles.backgroundColor, '#ffffff'),
        border: this.numberInRange(styles.borderWidth, 0, 8, 0) > 0 && block.type !== 'divider'
          ? `${styles.borderWidth}px solid ${this.safeColor(styles.borderColor, '#d8dee9')}`
          : '0',
        borderRadius: `${this.numberInRange(styles.borderRadius, 0, 32, 0)}px`
      }
    },
    dividerStyle(block) {
      return {
        borderTop: `${this.numberInRange(block.styles.borderWidth, 1, 8, 1)}px solid ${this.safeColor(block.styles.borderColor, this.settings.accentColor)}`
      }
    },
    setViewMode(mode) {
      if (mode === this.viewMode) return
      this.flushStructuredEdits()
      if (mode === 'developer') {
        this.refreshJsonTemplate()
      }
      this.viewMode = mode
    },
    activateSection(section) {
      this.activeSection = section
    },
    selectBlock(section, blockId) {
      this.activeSection = section
      this.activeBlockId = blockId
      this.viewMode = 'visual'
    },
    toggleAddMenu(section) {
      this.activeSection = section
      this.activeBlockId = ''
      this.addMenuSection = this.addMenuSection === section ? '' : section
    },
    closeAddMenu() {
      this.addMenuSection = ''
    },
    addBlockToSection(type, section) {
      this.activeSection = section
      this.addMenuSection = ''
      const previousBlock = this.sections[section][this.sections[section].length - 1]
      if (previousBlock && !this.blockHasContent(previousBlock)) {
        this.sections[section].pop()
      }
      this.addBlock(type)
    },
    addBlock(type) {
      const block = this.createBlock(type)
      this.sections[this.activeSection].push(block)
      this.activeBlockId = block.id
      this.emitContent()
      this.$nextTick(() => {
        const editor = this.getBlockEditor(block.id)
        if (editor && editor.focusAtEnd) editor.focusAtEnd()
      })
    },
    updateBlock(blockId, content) {
      const block = this.findBlock(blockId)
      if (!block || block.html === content) return
      block.html = content
      this.emitContent()
    },
    updateStructuredBlock(blockId, event) {
      const element = event && event.currentTarget
      if (!element) return
      const block = this.findBlock(blockId)
      const content = this.sanitizeHtml(element.innerHTML)
      if (!block || block.html === content) return
      block.html = content
      this.emitContent()
    },
    flushStructuredEdits() {
      if (!this.$el) return
      let changed = false
      this.$el.querySelectorAll('[data-structured-block-id]').forEach(element => {
        const block = this.findBlock(element.getAttribute('data-structured-block-id'))
        if (!block) return
        const content = this.sanitizeHtml(element.innerHTML)
        if (block.html === content) return
        block.html = content
        changed = true
      })
      if (changed) this.emitContent()
    },
    findBlock(blockId) {
      for (const key of SECTION_KEYS) {
        const block = this.sections[key].find(item => item.id === blockId)
        if (block) return block
      }
      return null
    },
    findBlockLocation(blockId) {
      for (const section of SECTION_KEYS) {
        const index = this.sections[section].findIndex(item => item.id === blockId)
        if (index !== -1) return { section, index }
      }
      return null
    },
    getBlockEditor(blockId) {
      const editor = this.$refs[`contractBlock-${blockId}`]
      return Array.isArray(editor) ? editor[0] : editor
    },
    queueToolbarDockSync() {
      this.$nextTick(() => this.syncToolbarDock())
    },
    syncToolbarDock() {
      const dock = this.$refs.quillToolbarDock
      const editor = this.activeBlock && !this.isUtilityBlock(this.activeBlock)
        ? this.getBlockEditor(this.activeBlockId)
        : null

      if (this._dockedEditor && this._dockedEditor !== editor && this._dockedEditor.restoreToolbar) {
        this._dockedEditor.restoreToolbar()
      }
      this._dockedEditor = null

      if (this.viewMode !== 'visual' || !dock || !editor || !editor.dockToolbar) return
      if (editor.dockToolbar(dock)) this._dockedEditor = editor
    },
    releaseToolbarDock() {
      if (this._dockedEditor && this._dockedEditor.restoreToolbar) {
        this._dockedEditor.restoreToolbar()
      }
      this._dockedEditor = null
    },
    moveActiveBlock(offset) {
      const location = this.findBlockLocation(this.activeBlockId)
      if (!location) return
      const blocks = this.sections[location.section]
      const target = location.index + offset
      if (target < 0 || target >= blocks.length) return
      const block = blocks.splice(location.index, 1)[0]
      blocks.splice(target, 0, block)
      this.emitContent()
    },
    duplicateActiveBlock() {
      const location = this.findBlockLocation(this.activeBlockId)
      if (!location) return
      const source = this.sections[location.section][location.index]
      const clone = this.createBlock(source.type, source.html, source.styles)
      this.sections[location.section].splice(location.index + 1, 0, clone)
      this.activeBlockId = clone.id
      this.emitContent()
    },
    deleteActiveBlock() {
      this.removeBlock(this.activeBlockId)
    },
    removeBlock(blockId) {
      const location = this.findBlockLocation(blockId)
      if (!location) return
      this.sections[location.section].splice(location.index, 1)
      const remaining = this.sections[location.section]
      this.activeBlockId = remaining.length ? remaining[Math.min(location.index, remaining.length - 1)].id : ''
      this.emitContent()
    },
    insertVariable(variableKey) {
      if (!this.activeBlock || this.isUtilityBlock(this.activeBlock)) {
        this.addBlock('text')
      }
      this.$nextTick(() => {
        const editor = this.getBlockEditor(this.activeBlockId)
        if (!editor) return
        editor.selectedVariable = variableKey
        editor.insertVariable()
      })
    },
    onVariableSelect() {
      if (!this.selectedVariableKey) return
      const variableKey = this.selectedVariableKey
      this.selectedVariableKey = ''
      this.insertVariable(variableKey)
    },
    normalizeVariable(value) {
      return String(value || '').replace(/\s/g, '')
    },
    variableLabel(variable) {
      const field = variable.key.replace(/[{}]/g, '').trim().split('.').pop()
      const labelKeys = {
        email: 'email', phone: 'phone', address: 'address', position: 'position', department: 'department',
        contract_number: 'contract_number', title: 'title', start_date: 'start_date', end_date: 'end_date',
        amount: 'amount', current_date: 'date', hire_date: 'hire_date'
      }
      if (field === 'name') {
        return /business|company/.test(variable.key) ? this.$t('business_name') : this.$t('recipient_name')
      }
      return labelKeys[field] ? this.$t(labelKeys[field]) : variable.label
    },
    preferredRecipientVariable(field) {
      const hasEmployeeVariables = this.variables.some(variable => this.normalizeVariable(variable.key) === `{{employee.${field}}}`)
      return hasEmployeeVariables ? `{{employee.${field}}}` : `{{entity.${field}}}`
    },
    hasVariable(variableKey) {
      const normalized = this.normalizeVariable(variableKey)
      return this.variables.some(variable => this.normalizeVariable(variable.key) === normalized)
    },
    documentHasMeaningfulContent() {
      const placeholder = this.$t('start_writing_here').trim()
      return SECTION_KEYS.some(key => this.sections[key].some(block => {
        if (this.isUtilityBlock(block)) return true
        const text = this.plainText(block.html)
        const hasMedia = /<(img|video|iframe|hr)\b/i.test(block.html || '')
        return hasMedia || (text.length > 0 && text !== placeholder)
      }))
    },
    async applyPreset(presetKey) {
      const hasContent = this.documentHasMeaningfulContent()
      if (hasContent && this.$swal && this.$swal.fire) {
        const result = await this.$swal.fire({
          title: this.$t('replace_current_layout'),
          text: this.$t('replace_layout_warning'),
          icon: 'warning',
          showCancelButton: true,
          confirmButtonText: this.$t('replace_layout'),
          cancelButtonText: this.$t('cancel')
        })
        if (!result.isConfirmed) return false
      }

      const preset = this.buildPreset(presetKey)
      this.settings = { ...this.defaultSettings(), ...preset.settings }
      this.sections = preset.sections
      this.activePresetKey = preset.key || ''
      if (preset.pagePadding !== undefined) this.$emit('update:pagePadding', preset.pagePadding)
      if (preset.pageMargin !== undefined) this.$emit('update:pageMargin', preset.pageMargin)
      this.activeSection = 'body'
      this.activeBlockId = this.sections.body.length ? this.sections.body[0].id : ''
      this.$emit('update:documentType', this.settings.documentType)
      this.emitContent()
      return true
    },
    async onPresetSelect(event) {
      const previousPresetKey = this.activePresetKey
      const applied = await this.applyPreset(event.target.value)
      if (!applied) event.target.value = previousPresetKey
    },
    buildPreset(key) {
      if (key === 'blank') {
        return {
          key: 'blank',
          settings: { documentType: 'contract' },
          sections: { header: [], body: [this.createBlock('text')], footer: [] }
        }
      }

      const jsonTemplate = this.findDefaultTemplate(key)
      if (jsonTemplate) return this.buildJsonPreset(jsonTemplate)

      if (key === 'certificate') {
        const recipientName = this.preferredRecipientVariable('name')
        return {
          settings: {
            documentType: 'certificate',
            fontFamily: 'Georgia, serif',
            accentColor: '#b2832f',
            fontSize: 15
          },
          sections: {
            header: [this.createBlock('image', '<p style="text-align: center;"><img src="{{business.logo}}" alt="Logo" style="max-width: 140px;"></p>')],
            body: [
              this.createBlock('heading', `<h1 style="text-align: center; font-size: 36px; letter-spacing: 3px;">${this.$t('certificate')}</h1><p style="text-align: center;">${this.$t('certificate_of_achievement')}</p>`),
              this.createBlock('divider', '', { borderColor: '#b2832f', borderWidth: 2, marginBottom: 28 }),
              this.createBlock('text', `<p style="text-align: center;">${this.$t('this_certifies_that')}</p><h2 style="text-align: center;">${recipientName}</h2><p style="text-align: center;">${this.$t('certificate_description_placeholder')}</p>`)
            ],
            footer: [this.createBlock('signature', `<p><strong>{{business.name}}</strong><br>${this.$t('signature')}: ____________________</p><p>${this.$t('date')}: {{current_date}}</p>`)]
          }
        }
      }

      return this.buildJsonPreset(defaultDocumentTemplates[0])
    },
    findDefaultTemplate(key) {
      const aliases = {
        contract: 'contract-business',
        attestation: 'attestation-classic'
      }
      const resolvedKey = aliases[key] || key
      return defaultDocumentTemplates.find(template => template.key === resolvedKey)
    },
    buildJsonPreset(template) {
      const page = template.page || {}
      const employeeTemplate = this.hasVariable('{{employee.position}}')
      const sections = {}
      SECTION_KEYS.forEach(sectionKey => {
        const blocks = template.sections && Array.isArray(template.sections[sectionKey])
          ? template.sections[sectionKey]
          : []
        sections[sectionKey] = blocks.map(block => {
          const sourceHtml = employeeTemplate && block.employeeHtml ? block.employeeHtml : block.html
          const renderedHtml = this.renderDefaultTemplateHtml(sourceHtml)
          const instanceHtml = this.allowSmartVariables
            ? renderedHtml
            : this.resolveVariableValues(renderedHtml, true)
          return this.createBlock(block.type, instanceHtml, block.styles || {})
        })
      })
      return {
        key: template.key,
        pagePadding: this.numberInRange(page.padding, 0, 64, 20),
        pageMargin: this.numberInRange(page.margin, 0, 48, 20),
        settings: {
          documentType: template.documentType,
          fontFamily: this.safeFont(page.fontFamily),
          fontSize: this.numberInRange(page.fontSize, 10, 20, 14),
          textColor: this.safeColor(page.textColor, '#1f2937'),
          accentColor: this.safeColor(page.accentColor, '#303030'),
          backgroundColor: this.safeColor(page.backgroundColor, '#ffffff')
        },
        sections
      }
    },
    renderDefaultTemplateHtml(html) {
      return String(html || '')
        .replace(/\[\[t:([a-zA-Z0-9_]+)\]\]/g, (match, key) => this.$t(key))
        .replace(/\[\[recipient:([a-zA-Z0-9_]+)\]\]/g, (match, field) => this.preferredRecipientVariable(field))
    },
    onDocumentTypeChange() {
      this.$emit('update:documentType', this.settings.documentType)
      this.emitContent()
    },
    blockHasContent(block) {
      if (block.type === 'divider' || block.type === 'spacer') return true
      return this.plainText(block.html).length > 0 || /<(img|video|iframe|hr)\b/i.test(block.html)
    },
    sectionHasContent(section) {
      return this.sections[section].some(block => this.blockHasContent(block))
    },
    plainText(content) {
      return (content || '').replace(/<[^>]*>/g, '').replace(/&nbsp;/g, ' ').trim()
    },
    stripOuterWrapper(content) {
      if (!content || !content.includes('contract-page-wrapper')) return content || ''
      const parser = new DOMParser()
      const parsedDocument = parser.parseFromString(content, 'text/html')
      const wrapper = parsedDocument.querySelector('.contract-page-wrapper')
      return wrapper ? wrapper.innerHTML : content
    },
    resolveVariableValues(content, stripTokenMarkup = false) {
      let resolvedContent = String(content || '')
      Object.keys(this.variableValues).forEach(key => {
        const value = this.variableValues[key] === undefined || this.variableValues[key] === null
          ? ''
          : this.variableValues[key]
        const replaceVariable = variableKey => {
          const escapedKey = variableKey.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
          resolvedContent = resolvedContent.replace(new RegExp(escapedKey, 'g'), this.escapeHtml(value))
        }
        replaceVariable(key)

        const entityMatch = key.match(/^\{\{entity\.([a-zA-Z0-9_]+)\}\}$/)
        if (entityMatch) {
          ['client', 'customer', 'supplier', 'employee'].forEach(prefix => {
            replaceVariable(`{{${prefix}.${entityMatch[1]}}}`)
          })
        }
      })

      if (!stripTokenMarkup || !resolvedContent.includes('smart-variable-token')) return resolvedContent
      const parser = new DOMParser()
      const parsedDocument = parser.parseFromString(`<div>${resolvedContent}</div>`, 'text/html')
      const root = parsedDocument.body.firstElementChild
      root.querySelectorAll('.smart-variable-token').forEach(token => {
        token.replaceWith(...Array.from(token.childNodes))
      })
      return root.innerHTML
    },
    hydrateSections(content) {
      this.activePresetKey = ''
      let html = this.stripOuterWrapper(content)
      if (!this.allowSmartVariables) html = this.resolveVariableValues(html, true)
      const parser = new DOMParser()
      const parsedDocument = parser.parseFromString(html || '', 'text/html')
      const sectionRoot = parsedDocument.querySelector('.contract-document-sections')

      this.settings = this.defaultSettings()
      if (!sectionRoot) {
        this.sections = {
          header: [],
          body: html && this.plainText(html) ? [this.createBlock('text', html)] : [],
          footer: []
        }
        this.activeBlockId = this.sections.body.length ? this.sections.body[0].id : ''
        return
      }

      this.settings = this.readDocumentSettings(sectionRoot)
      this.sections = {}
      SECTION_KEYS.forEach(key => {
        const element = sectionRoot.querySelector(`[data-contract-section="${key}"]`)
          || sectionRoot.querySelector(`.contract-section-${key}`)
        this.$set(this.sections, key, this.readSectionBlocks(element))
      })
      const first = SECTION_KEYS.reduce((result, key) => result || this.sections[key][0], null)
      this.activeBlockId = first ? first.id : ''
      const location = first ? this.findBlockLocation(first.id) : null
      if (location) this.activeSection = location.section
      this.$emit('update:documentType', this.settings.documentType)
    },
    readDocumentSettings(root) {
      const defaults = this.defaultSettings()
      return {
        documentType: DOCUMENT_TYPES.includes(root.dataset.documentType) ? root.dataset.documentType : defaults.documentType,
        fontFamily: root.style.fontFamily || defaults.fontFamily,
        fontSize: this.numberInRange(parseFloat(root.style.fontSize), 10, 20, defaults.fontSize),
        textColor: this.safeColor(root.style.color, defaults.textColor),
        accentColor: this.safeColor(root.style.getPropertyValue('--contract-accent'), defaults.accentColor),
        backgroundColor: this.safeColor(root.style.backgroundColor, defaults.backgroundColor)
      }
    },
    readSectionBlocks(sectionElement) {
      if (!sectionElement) return []
      const blockElements = Array.from(sectionElement.children).filter(element => element.hasAttribute('data-contract-block'))
      if (!blockElements.length) {
        return this.blockHasContent({ type: 'text', html: sectionElement.innerHTML })
          ? [this.createBlock('text', sectionElement.innerHTML)]
          : []
      }
      return blockElements.map(element => this.createBlock(
        element.dataset.contractBlockType || 'text',
        element.innerHTML,
        this.readBlockStyles(element)
      ))
    },
    readBlockStyles(element) {
      return {
        padding: parseFloat(element.style.padding),
        marginBottom: parseFloat(element.style.marginBottom),
        backgroundColor: element.style.backgroundColor,
        borderColor: element.style.borderColor,
        borderWidth: parseFloat(element.style.borderWidth),
        borderRadius: parseFloat(element.style.borderRadius),
        height: parseFloat(element.dataset.blockHeight)
      }
    },
    serializeSections() {
      const rootStyle = [
        `font-family: ${this.safeFont(this.settings.fontFamily)}`,
        `font-size: ${this.numberInRange(this.settings.fontSize, 10, 20, 14)}px`,
        `color: ${this.safeColor(this.settings.textColor, '#1f2937')}`,
        `background-color: ${this.safeColor(this.settings.backgroundColor, '#ffffff')}`,
        `--contract-accent: ${this.safeColor(this.settings.accentColor, '#303030')}`
      ].join('; ')

      const serializeSection = key => {
        const classes = ['contract-section', `contract-section-${key}`]
        if (!this.sectionHasContent(key)) classes.push('contract-section-empty')
        const blocks = this.sections[key].map(block => this.serializeBlock(block)).join('')
        const tag = key === 'header' ? 'header' : key === 'footer' ? 'footer' : 'main'
        return `<${tag} class="${classes.join(' ')}" data-contract-section="${key}">${blocks}</${tag}>`
      }

      return `<div class="contract-document-sections contract-builder-v2" data-contract-builder-version="2" data-document-type="${this.escapeAttribute(this.settings.documentType)}" style="${this.escapeAttribute(rootStyle)}">${SECTION_KEYS.map(serializeSection).join('')}</div>`
    },
    serializeBlock(block) {
      const styles = this.normalizeBlockStyles(block.styles, block.type)
      const style = [
        `padding: ${styles.padding}px`,
        `margin-bottom: ${styles.marginBottom}px`,
        `background-color: ${styles.backgroundColor}`,
        `border: ${styles.borderWidth}px solid ${styles.borderColor}`,
        `border-radius: ${styles.borderRadius}px`
      ].join('; ')
      let html = this.restoreAssetVariables(this.sanitizeHtml(block.html || ''))
      if (block.type === 'divider') {
        html = `<hr style="margin: 0; border: 0; border-top: ${Math.max(styles.borderWidth, 1)}px solid ${styles.borderColor};">`
      } else if (block.type === 'spacer') {
        html = `<div style="height: ${styles.height}px;"></div>`
      }
      return `<div class="contract-block contract-block-${this.escapeAttribute(block.type)}" data-contract-block="true" data-contract-block-id="${this.escapeAttribute(block.id)}" data-contract-block-type="${this.escapeAttribute(block.type)}" data-block-height="${styles.height}" style="${this.escapeAttribute(style)}">${html}</div>`
    },
    emitContent() {
      this.lastEmittedValue = this.serializeSections()
      this.$emit('input', this.lastEmittedValue)
      this.$emit('change', this.exportDocumentObject())
      if (!this.jsonDirty) this.refreshJsonTemplate()
    },
    markChanged() {
      this.emitContent()
    },
    exportDocumentObject() {
      const sections = {}
      SECTION_KEYS.forEach(key => {
        sections[key] = this.sections[key].map(block => ({
          id: block.id,
          type: block.type,
          html: this.restoreAssetVariables(block.html),
          styles: { ...block.styles }
        }))
      })
      return {
        schema: SCHEMA_NAME,
        version: 2,
        documentType: this.settings.documentType,
        page: {
          padding: Number(this.pagePadding),
          margin: Number(this.pageMargin),
          fontFamily: this.settings.fontFamily,
          fontSize: Number(this.settings.fontSize),
          textColor: this.settings.textColor,
          accentColor: this.settings.accentColor,
          backgroundColor: this.settings.backgroundColor
        },
        sections
      }
    },
    exportDocumentJson() {
      return JSON.stringify(this.exportDocumentObject(), null, 2)
    },
    refreshJsonTemplate() {
      this.jsonTemplate = this.exportDocumentJson()
      this.jsonDirty = false
      this.jsonError = ''
    },
    validateJsonTemplate(parsed) {
      if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) throw new Error(this.$t('json_must_be_object'))
      if (parsed.schema && parsed.schema !== SCHEMA_NAME) throw new Error(this.$t('unsupported_template_schema'))
      if (!parsed.sections || typeof parsed.sections !== 'object') throw new Error(this.$t('json_sections_required'))
      const documentType = parsed.documentType || parsed.document_type || 'custom'
      if (!DOCUMENT_TYPES.includes(documentType)) throw new Error(this.$t('invalid_document_type'))

      const page = parsed.page && typeof parsed.page === 'object' ? parsed.page : {}
      const settings = {
        documentType,
        fontFamily: this.safeFont(page.fontFamily || this.settings.fontFamily),
        fontSize: this.numberInRange(page.fontSize, 10, 20, 14),
        textColor: this.safeColor(page.textColor, '#1f2937'),
        accentColor: this.safeColor(page.accentColor, '#303030'),
        backgroundColor: this.safeColor(page.backgroundColor, '#ffffff')
      }
      const sections = {}
      let blockCount = 0
      const blockIds = new Set()
      SECTION_KEYS.forEach(key => {
        const blocks = parsed.sections[key] == null ? [] : parsed.sections[key]
        if (!Array.isArray(blocks)) throw new Error(this.$t('json_section_must_be_array', { section: key }))
        sections[key] = blocks.map(rawBlock => {
          if (!rawBlock || typeof rawBlock !== 'object') throw new Error(this.$t('invalid_json_block'))
          blockCount += 1
          if (blockCount > 200) throw new Error(this.$t('too_many_blocks'))
          const type = BLOCK_TYPES.includes(rawBlock.type) ? rawBlock.type : 'text'
          const block = this.createBlock(type, String(rawBlock.html || ''), rawBlock.styles || {})
          if (rawBlock.id && /^[a-zA-Z0-9_-]{1,80}$/.test(String(rawBlock.id)) && !blockIds.has(String(rawBlock.id))) {
            block.id = String(rawBlock.id)
          }
          blockIds.add(block.id)
          return block
        })
      })

      return {
        settings,
        sections,
        padding: this.numberInRange(page.padding, 0, 64, Number(this.pagePadding)),
        margin: this.numberInRange(page.margin, 0, 48, Number(this.pageMargin))
      }
    },
    applyJsonTemplate() {
      try {
        const parsed = JSON.parse(this.jsonTemplate)
        const normalized = this.validateJsonTemplate(parsed)
        this.settings = normalized.settings
        this.sections = normalized.sections
        this.$emit('update:pagePadding', normalized.padding)
        this.$emit('update:pageMargin', normalized.margin)
        this.$emit('update:documentType', normalized.settings.documentType)
        const first = SECTION_KEYS.reduce((result, key) => result || this.sections[key][0], null)
        this.activeBlockId = first ? first.id : ''
        const location = first ? this.findBlockLocation(first.id) : null
        this.activeSection = location ? location.section : 'body'
        this.jsonDirty = false
        this.jsonError = ''
        this.$nextTick(() => {
          this.emitContent()
          this.refreshJsonTemplate()
        })
        this.showToast(this.$t('json_applied_successfully'), 'success')
      } catch (error) {
        this.jsonError = `${this.$t('invalid_json')}: ${error.message}`
      }
    },
    formatJson() {
      try {
        this.jsonTemplate = JSON.stringify(JSON.parse(this.jsonTemplate), null, 2)
        this.jsonError = ''
      } catch (error) {
        this.jsonError = `${this.$t('invalid_json')}: ${error.message}`
      }
    },
    async copyJson() {
      try {
        if (navigator.clipboard && navigator.clipboard.writeText) {
          await navigator.clipboard.writeText(this.jsonTemplate)
        } else {
          const textarea = this.$el.querySelector('.json-editor')
          textarea.select()
          document.execCommand('copy')
        }
        this.showToast(this.$t('copied_successfully'), 'success')
      } catch (error) {
        this.showToast(this.$t('copy_failed'), 'error')
      }
    },
    showToast(message, type) {
      if (this.$toast && this.$toast.open) this.$toast.open({ message, type })
    },
    normalizeBlockStyles(styles, type) {
      const defaults = this.defaultBlockStyles(type)
      return {
        padding: this.numberInRange(styles.padding, 0, 48, defaults.padding),
        marginBottom: this.numberInRange(styles.marginBottom, 0, 64, defaults.marginBottom),
        backgroundColor: this.safeColor(styles.backgroundColor, defaults.backgroundColor),
        borderColor: this.safeColor(styles.borderColor, defaults.borderColor),
        borderWidth: this.numberInRange(styles.borderWidth, 0, 8, defaults.borderWidth),
        borderRadius: this.numberInRange(styles.borderRadius, 0, 32, defaults.borderRadius),
        height: this.numberInRange(styles.height, 0, 160, defaults.height)
      }
    },
    numberInRange(value, minimum, maximum, fallback) {
      const number = Number(value)
      if (!Number.isFinite(number)) return fallback
      return Math.min(Math.max(number, minimum), maximum)
    },
    safeColor(value, fallback) {
      const color = String(value || '').trim()
      return /^(#[0-9a-f]{3,8}|rgba?\([\d\s.,%]+\)|hsla?\([\d\s.,%]+\)|transparent|white|black)$/i.test(color) ? color : fallback
    },
    safeFont(value) {
      const font = String(value || '')
      return this.fontOptions.some(option => option.value === font) ? font : this.fontOptions[0].value
    },
    resolveEditorAssets(html) {
      if (!this.business.logo) return html
      return String(html || '').replace(/\{\{business\.logo\}\}/g, this.escapeAttribute(this.business.logo))
    },
    restoreAssetVariables(html) {
      if (!this.business.logo || !html) return html
      const parser = new DOMParser()
      const parsedDocument = parser.parseFromString(`<div>${html}</div>`, 'text/html')
      const root = parsedDocument.body.firstElementChild
      root.querySelectorAll('img').forEach(image => {
        if (image.getAttribute('src') === this.business.logo || image.src === this.business.logo) {
          image.setAttribute('src', '{{business.logo}}')
        }
      })
      return root.innerHTML
    },
    sanitizeHtml(html) {
      const parser = new DOMParser()
      const parsedDocument = parser.parseFromString(`<div>${String(html || '')}</div>`, 'text/html')
      const root = parsedDocument.body.firstElementChild
      const allowedTags = new Set([
        'P', 'BR', 'H1', 'H2', 'H3', 'H4', 'H5', 'H6', 'STRONG', 'B', 'EM', 'I', 'U', 'S', 'SPAN', 'DIV',
        'UL', 'OL', 'LI', 'BLOCKQUOTE', 'A', 'IMG', 'VIDEO', 'IFRAME', 'HR', 'TABLE', 'THEAD', 'TBODY',
        'TFOOT', 'TR', 'TH', 'TD', 'CAPTION', 'SUB', 'SUP', 'PRE', 'CODE'
      ])
      const allowedAttributes = new Set([
        'class', 'style', 'href', 'src', 'alt', 'title', 'target', 'rel', 'colspan', 'rowspan', 'width', 'height',
        'controls', 'poster', 'allow', 'allowfullscreen', 'frameborder'
      ])
      const allowedStyleProperties = new Set([
        'color', 'background-color', 'font-family', 'font-size', 'font-weight', 'font-style', 'text-align',
        'text-decoration', 'text-transform', 'line-height', 'letter-spacing', 'white-space', 'vertical-align',
        'width', 'height', 'min-width', 'max-width', 'min-height', 'max-height', 'margin', 'margin-top',
        'margin-right', 'margin-bottom', 'margin-left', 'padding', 'padding-top', 'padding-right', 'padding-bottom',
        'padding-left', 'border', 'border-width', 'border-style', 'border-color', 'border-radius', 'border-top',
        'border-right', 'border-bottom', 'border-left', 'display', 'flex', 'flex-direction', 'flex-wrap',
        'align-items', 'justify-content', 'gap', 'grid-template-columns', 'grid-column', 'grid-row'
      ])

      Array.from(root.querySelectorAll('*')).forEach(element => {
        if (!allowedTags.has(element.tagName)) {
          // Keep readable contract text when importing legacy/third-party HTML.
          // Removing an unsupported wrapper must not remove all its descendants.
          element.replaceWith(...Array.from(element.childNodes))
          return
        }
        Array.from(element.attributes).forEach(attribute => {
          const name = attribute.name.toLowerCase()
          const value = attribute.value.trim()
          if (!allowedAttributes.has(name) || name.startsWith('on') || name === 'srcdoc') {
            element.removeAttribute(attribute.name)
            return
          }
          if (name === 'href' || name === 'src' || name === 'poster') {
            const isTemplateVariable = /^\{\{[a-zA-Z0-9_.]+\}\}$/.test(value)
            const isSafeUrl = /^(https?:|mailto:|tel:|\/|#|\.\/|\.\.\/)/i.test(value)
            const isSafeImageData = element.tagName === 'IMG' && /^data:image\/(png|jpe?g|gif|webp);base64,/i.test(value)
            if (!isTemplateVariable && !isSafeUrl && !isSafeImageData) element.removeAttribute(attribute.name)
          }
          if (name === 'style') {
            Array.from(element.style).forEach(property => {
              if (!allowedStyleProperties.has(property) || /url\s*\(|expression\s*\(/i.test(element.style.getPropertyValue(property))) {
                element.style.removeProperty(property)
              }
            })
            if (!element.getAttribute('style')) element.removeAttribute('style')
          }
        })
      })
      return root.innerHTML
    },
    escapeHtml(value) {
      return String(value)
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#039;')
    },
    escapeAttribute(value) {
      return this.escapeHtml(value).replace(/`/g, '&#096;')
    }
  }
}
</script>

<style scoped>
.contract-section-builder {
  --builder-primary: #303030;
  background: #f5f7fb;
  border: 1px solid #e3e7ef;
  border-radius: 16px;
  overflow: hidden;
}

.contract-section-builder .btn-primary {
  background-color: var(--builder-primary);
  border-color: var(--builder-primary);
}

.contract-section-builder .btn-primary:hover,
.contract-section-builder .btn-primary:focus {
  background-color: #1f1f1f;
  border-color: #1f1f1f;
}

.contract-section-builder .btn-outline-primary {
  color: var(--builder-primary);
  border-color: var(--builder-primary);
}

.contract-section-builder .btn-outline-primary:hover,
.contract-section-builder .btn-outline-primary:focus {
  color: #fff;
  background-color: var(--builder-primary);
  border-color: var(--builder-primary);
}

.contract-section-builder .text-primary {
  color: var(--builder-primary) !important;
}

.contract-section-builder .form-control:focus,
.contract-section-builder .form-select:focus {
  border-color: var(--builder-primary);
  box-shadow: 0 0 0 0.2rem rgba(48, 48, 48, 0.16);
}

.contract-section-builder .form-range {
  accent-color: var(--builder-primary);
}

.developer-header,
.sidebar-heading,
.sidebar-summary {
  display: flex;
  align-items: center;
}

.builder-command-bar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 18px;
  padding: 12px 14px;
  background: #fff;
  border-bottom: 1px solid #e3e7ef;
}
.builder-command-copy small { color: #788397; }
.view-mode-switcher {
  display: inline-flex;
  gap: 3px;
  padding: 3px;
  background: #f1f4f8;
  border: 1px solid #e2e7ee;
  border-radius: 10px;
}
.view-mode-button {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 7px;
  min-width: 86px;
  height: 34px;
  padding: 0 12px;
  color: #657084;
  font-size: 0.76rem;
  font-weight: 650;
  background: transparent;
  border: 0;
  border-radius: 7px;
}
.view-mode-button:hover { color: #1f2937; background: #fff; }
.view-mode-button.active { color: #fff; background: #303030; box-shadow: 0 2px 7px rgba(31, 41, 55, 0.2); }
.min-width-0 { min-width: 0; }

.developer-header small { color: #6f7787; }
.builder-workspace {
  display: grid;
  grid-template-columns: minmax(0, 1fr) 280px;
  gap: 12px;
  padding: 12px;
}
.builder-workspace.single-column { grid-template-columns: minmax(0, 1fr); }
.editor-column { min-width: 0; }
.quill-toolbar-dock:empty { display: none; }
.quill-toolbar-dock:not(:empty) {
  position: relative;
  z-index: 40;
  margin: 0 0 10px;
  overflow: visible;
  background: #fff;
  border: 1px solid #dfe3eb;
  border-radius: 10px;
  box-shadow: 0 4px 14px rgba(31, 38, 55, 0.06);
}
.quill-toolbar-dock::v-deep .ql-toolbar.ql-snow {
  position: relative !important;
  z-index: 41;
  display: flex !important;
  flex-wrap: wrap;
  align-items: center;
  min-height: 44px;
  padding: 7px 10px;
  overflow: visible;
  white-space: normal;
  background: transparent;
  border: 0 !important;
}
.quill-toolbar-dock::v-deep .ql-toolbar .ql-picker { font-size: 13px; }
.quill-toolbar-dock::v-deep .ql-toolbar .ql-picker.ql-expanded { z-index: 1000; }
.quill-toolbar-dock::v-deep .ql-toolbar .ql-picker-options { z-index: 1001; }
.quill-toolbar-dock::v-deep .ql-toolbar .ql-font { width: 150px !important; }
.quill-toolbar-dock::v-deep .ql-toolbar .ql-size { width: 70px !important; }
.quill-toolbar-dock::v-deep .ql-toolbar button {
  width: 28px;
  height: 28px;
  padding: 3px;
  border-radius: 4px;
}
.quill-toolbar-dock::v-deep .ql-toolbar button:hover { background: #f0f0f0; }
.quill-toolbar-dock::v-deep .ql-toolbar button.ql-active { color: #334155; background: #f1f1f1; }
.unified-document-canvas,
.contract-preview-canvas {
  max-height: calc(100vh - 225px);
  padding: 16px;
  overflow: auto;
  background: #dfe3ea;
  border-radius: 12px;
}
.unified-document-page,
.contract-preview-page {
  display: flex;
  flex-direction: column;
  width: 100%;
  max-width: 210mm;
  min-height: 1123px;
  line-height: 1.58;
  box-sizing: border-box;
  box-shadow: 0 8px 30px rgba(32, 39, 55, 0.14);
}
.document-section-shell { position: relative; flex: 0 0 auto; padding: 16px 0; transition: 0.18s ease; }
.document-section-shell-body { flex: 1 1 auto; padding-top: 28px; padding-bottom: 28px; }
.document-section-shell-body.empty {
  display: flex;
  min-height: 620px;
  flex-direction: column;
  justify-content: flex-start;
}
.document-section-shell-body.empty .section-add-control.in-flow {
  margin-top: 0;
}
.document-section-shell-header.empty,
.document-section-shell-footer.empty {
  min-height: 110px;
}
.document-section-shell-header { border-bottom: 2px dashed #94a3b8; }
.document-section-shell-footer { margin-top: auto; border-top: 2px dashed #94a3b8; }
.section-label {
  position: absolute;
  top: 4px;
  left: 6px;
  z-index: 2;
  padding: 2px 6px;
  color: #8a92a1;
  font: 700 9px/1.4 Arial, sans-serif;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  background: rgba(255, 255, 255, 0.9);
  border-radius: 4px;
  opacity: 0.85;
  transition: opacity 0.18s ease;
}
.document-section-shell:hover .section-label,
.document-section-shell.active .section-label { opacity: 1; }
.document-block-shell {
  position: relative;
  min-height: 36px;
  border: 1px solid transparent;
  transition: opacity 0.18s ease, filter 0.18s ease, border-color 0.16s ease, box-shadow 0.16s ease;
}
.document-block-shell:hover { border-color: #bdbdbd; }
.document-block-shell.active {
  z-index: 3;
  border: 1px dashed #71809b;
}
.section-add-control {
  position: absolute;
  left: 50%;
  bottom: 4px;
  z-index: 100;
  visibility: hidden;
  opacity: 0;
  pointer-events: none;
  transform: translateX(-50%);
  transition: opacity 0.16s ease, visibility 0.16s ease;
}
.document-section-shell:hover > .section-add-control,
.document-section-shell:focus-within > .section-add-control,
.section-add-control.open {
  visibility: visible;
  opacity: 1;
  pointer-events: auto;
}
.section-add-control.open { z-index: 1200; }
.section-add-control.in-flow {
  position: relative;
  left: auto;
  bottom: auto;
  width: max-content;
  margin: 8px auto 0;
  transform: none;
}
.section-add-button {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 5px;
  height: 30px;
  padding: 0 10px;
  color: #71809b;
  font: 600 0.72rem Arial, sans-serif;
  background: #fff;
  border: 1px solid #cbd5e5;
  border-radius: 7px;
  box-shadow: 0 2px 8px rgba(31, 38, 55, 0.1);
}
.section-add-button .bi { font-size: 0.62rem; }
.section-add-button:hover { color: #334155; background: #f1f1f1; border-color: #bdbdbd; }
.section-add-menu {
  position: absolute;
  top: calc(100% + 5px);
  left: 50%;
  z-index: 1201;
  display: grid;
  width: 175px;
  padding: 6px;
  background: #fff;
  border: 1px solid #dfe3eb;
  border-radius: 9px;
  box-shadow: 0 10px 28px rgba(31, 38, 55, 0.16);
  transform: translateX(-50%);
}
.section-add-option {
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  padding: 7px 9px;
  color: #4b5566;
  font-size: 0.75rem;
  text-align: left;
  background: transparent;
  border: 0;
  border-radius: 6px;
}
.section-add-option:hover { color: #303030; background: #f1f1f1; }
.section-add-option .bi { width: 16px; color: #334155; text-align: center; }
.utility-divider { width: 100%; margin: 16px 0; }
.utility-spacer { display: flex; align-items: center; justify-content: center; color: #9aa3b2; border: 1px dashed #d5dae3; }
.utility-spacer span { font: 600 10px Arial, sans-serif; }

.document-block-shell::v-deep .contract-editor-embedded { min-width: 0; }
.document-block-shell::v-deep .ql-container.ql-snow { border: 0 !important; font-family: inherit; font-size: inherit; }
.document-block-shell::v-deep .ql-editor { min-height: 0 !important; padding: 0 !important; color: inherit; line-height: inherit; background: transparent; }
.structured-html-editor { min-height: 100px; color: inherit; line-height: inherit; outline: none; }
.structured-html-editor::v-deep table { width: 100%; border-collapse: collapse; }
.structured-html-editor::v-deep th,
.structured-html-editor::v-deep td { padding: 6px; vertical-align: top; }
.document-block-shell::v-deep .ql-toolbar.ql-snow { display: none; }
.document-block-shell.active::v-deep .ql-toolbar.ql-snow {
  position: sticky;
  top: 0;
  z-index: 20;
  display: block;
  min-height: 44px;
  overflow-x: auto;
  white-space: nowrap;
  background: #fff;
  border: 1px solid #dfe3eb !important;
  border-radius: 8px;
  box-shadow: 0 4px 14px rgba(31, 38, 55, 0.08);
}

.builder-sidebar { display: flex; flex-direction: column; gap: 10px; max-height: calc(100vh - 258px); padding-right: 2px; overflow-y: auto; }
.sidebar-block { padding: 14px; background: #fff; border: 1px solid #e3e7ef; border-radius: 12px; }
.sidebar-heading { gap: 9px; margin-bottom: 12px; }
.sidebar-icon { display: inline-flex; align-items: center; justify-content: center; width: 32px; height: 32px; background: #f1f1f1; border-radius: 8px; }
.starter-layout-list { display: grid; gap: 6px; }
.starter-layout-button {
  display: flex;
  align-items: center;
  gap: 9px;
  width: 100%;
  padding: 8px 10px;
  color: #536074;
  font-size: 0.75rem;
  font-weight: 600;
  text-align: start;
  background: #f8fafc;
  border: 1px solid #e5e9ef;
  border-radius: 8px;
}
.starter-layout-button:hover { color: #303030; background: #f1f3f6; border-color: #cbd2dc; }
.starter-layout-button.active { color: #004aad; background: #eef5ff; border-color: #b9d4fa; }
.starter-layout-button > .bi:first-child { width: 18px; color: #004aad; text-align: center; }
.section-target-switcher {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 3px;
  padding: 3px;
  margin-bottom: 10px;
  background: #f1f4f7;
  border-radius: 8px;
}
.section-target-switcher button {
  min-width: 0;
  padding: 6px 3px;
  overflow: hidden;
  color: #697386;
  font-size: 0.68rem;
  font-weight: 650;
  text-overflow: ellipsis;
  white-space: nowrap;
  background: transparent;
  border: 0;
  border-radius: 6px;
}
.section-target-switcher button.active { color: #253248; background: #fff; box-shadow: 0 1px 4px rgba(30, 41, 59, 0.12); }
.quick-block-grid { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 6px; }
.quick-block-button {
  display: flex;
  align-items: center;
  gap: 7px;
  min-width: 0;
  padding: 8px;
  color: #536074;
  font-size: 0.71rem;
  text-align: start;
  background: #fff;
  border: 1px solid #e3e7ed;
  border-radius: 8px;
}
.quick-block-button:hover { color: #004aad; background: #f7faff; border-color: #b9d4fa; }
.quick-block-button .bi { width: 15px; color: #004aad; text-align: center; }
.selected-block-panel.empty { background: #fbfcfd; }
.selected-block-panel .sidebar-heading small { display: block; margin-top: 2px; color: #8992a2; font-size: 0.68rem; }
.block-action-grid { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 6px; }
.block-action-grid .block-action-button {
  gap: 6px;
  height: 34px;
  padding: 0 8px;
  font-size: 0.69rem;
}
.block-action-button:disabled { cursor: not-allowed; opacity: 0.45; }
.block-spacing-grid { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 8px; padding-top: 11px; margin-top: 11px; border-top: 1px solid #edf0f4; }
.block-spacing-grid label > span,
.appearance-field > span,
.appearance-color-grid label > span { display: block; margin-bottom: 4px; color: #697386; font-size: 0.67rem; font-weight: 700; }
.block-spacing-grid .form-control,
.appearance-fields .form-control { min-width: 0; }
.block-spacing-grid .input-group-text,
.appearance-fields .input-group-text { padding-right: 6px; padding-left: 6px; color: #7a8393; font-size: 0.65rem; }
.appearance-panel summary { cursor: pointer; list-style: none; }
.appearance-panel summary::-webkit-details-marker { display: none; }
.appearance-panel[open] .config-chevron { transform: rotate(180deg); }
.appearance-fields { padding-top: 12px; margin-top: 10px; border-top: 1px solid #edf0f4; }
.appearance-field { display: block; min-width: 0; }
.appearance-field-wide { margin-bottom: 10px; }
.appearance-grid { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 8px; }
.appearance-color-grid { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 8px; margin-top: 10px; }
.appearance-color-grid label { display: flex; align-items: center; justify-content: space-between; gap: 8px; padding: 7px; margin: 0; background: #f8f9fb; border: 1px solid #e7eaf0; border-radius: 8px; }
.appearance-color-grid label > span { margin: 0; }
.appearance-color-grid .form-control-color { width: 34px; height: 28px; padding: 2px; }
.view-mode-panel { padding: 4px; }
.icon-mode-switcher { display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: 2px; }
.mode-icon-button {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  height: 30px;
  color: #657084;
  font-size: 0.88rem;
  background: transparent;
  border: 1px solid transparent;
  border-radius: 8px;
}
.mode-icon-button:hover { color: #303030; background: #f3f4f6; }
.mode-icon-button.active { color: #fff; background: #303030; border-color: #303030; }
.add-content-panel.collapsed { align-self: flex-end; width: 46px; padding: 6px; }
.add-content-toggle { display: flex; align-items: center; gap: 9px; width: 100%; padding: 0; color: #303030; text-align: left; background: transparent; border: 0; }
.add-content-toggle:not(.expanded) { justify-content: center; }
.add-content-toggle.expanded { margin-bottom: 12px; }
.add-content-toggle .sidebar-icon { flex: 0 0 32px; }
.block-palette { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 7px; }
.preset-list { display: grid; gap: 8px; }
.preset-button { display: grid; grid-template-columns: 34px minmax(0, 1fr) auto; align-items: center; gap: 9px; width: 100%; padding: 9px; color: #4b5566; text-align: left; background: #f8f9fc; border: 1px solid #e5e8ef; border-radius: 9px; }
.preset-button:hover, .preset-button.active { color: #334155; background: #f1f1f1; border-color: #bdbdbd; }
.preset-icon { display: inline-flex; align-items: center; justify-content: center; width: 34px; height: 34px; color: #303030; background: #fff; border: 1px solid #dfe4f2; border-radius: 8px; }
.preset-copy { display: flex; min-width: 0; flex-direction: column; }
.preset-copy strong { font-size: 0.77rem; }
.preset-copy small { overflow: hidden; color: #8991a0; font-size: 0.66rem; text-overflow: ellipsis; white-space: nowrap; }
.preset-arrow { color: #a1a8b5; font-size: 0.7rem; }
.palette-button { display: flex; align-items: center; gap: 7px; padding: 8px; color: #4b5566; font-size: 0.75rem; text-align: left; background: #f8f9fc; border: 1px solid #e5e8ef; border-radius: 8px; }
.palette-button:hover { color: #334155; background: #f1f1f1; border-color: #bdbdbd; }
.palette-button i { color: #334155; }
.sidebar-hint { display: block; margin-top: 9px; color: #8991a0; }
.sidebar-summary { justify-content: space-between; font-size: 0.82rem; font-weight: 700; cursor: pointer; list-style: none; }
.sidebar-summary::-webkit-details-marker { display: none; }
.config-summary { margin: -4px; padding: 4px; border-radius: 8px; }
.config-summary:hover { background: #f7f8fa; }
.config-summary-end { display: inline-flex; align-items: center; gap: 7px; min-width: 0; }
.config-summary-end small { max-width: 72px; overflow: hidden; color: #8b93a3; font-size: 0.65rem; font-weight: 600; text-overflow: ellipsis; white-space: nowrap; }
.config-chevron { color: #8b93a3; font-size: 0.7rem; transition: transform 0.18s ease; }
.config-accordion[open] .config-chevron { transform: rotate(180deg); }
.compact-config-body { padding-top: 12px; margin-top: 10px; border-top: 1px solid #edf0f4; }
.compact-config-field { display: block; min-width: 0; margin: 0; }
.compact-config-field > span,
.compact-color-field > span {
  display: block;
  margin-bottom: 4px;
  color: #697386;
  font-size: 0.68rem;
  font-weight: 700;
  line-height: 1.2;
}
.compact-config-field-wide { margin-bottom: 10px; }
.compact-config-grid { display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: 7px; }
.compact-block-grid { grid-template-columns: repeat(2, minmax(0, 1fr)); }
.compact-config-grid .form-control { min-width: 0; padding-right: 4px; padding-left: 6px; }
.compact-config-grid .input-group-text { padding: 0 5px; color: #7a8393; font-size: 0.65rem; }
.compact-color-grid { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 7px; margin-top: 10px; }
.compact-color-field {
  display: grid;
  grid-template-columns: minmax(0, 1fr) 34px;
  align-items: center;
  gap: 6px;
  min-width: 0;
  padding: 6px 7px;
  margin: 0;
  background: #f8f9fb;
  border: 1px solid #e7eaf0;
  border-radius: 7px;
}
.compact-color-field > span { margin: 0; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.compact-color-field .form-control-color { width: 34px; height: 28px; padding: 2px; }
.compact-color-control { margin: 0; }
.compact-block-actions { display: grid; grid-template-columns: repeat(4, minmax(0, 1fr)); gap: 6px; padding-top: 10px; margin-top: 10px; border-top: 1px solid #edf0f4; }
.block-action-button {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  height: 30px;
  color: #657084;
  background: #f8f9fb;
  border: 1px solid #e4e7ed;
  border-radius: 7px;
}
.block-action-button:hover { color: #303030; background: #f0f1f3; border-color: #cfd4dc; }
.block-action-button.danger:hover { color: #c73535; background: #fff0f0; border-color: #f0caca; }
.color-control { display: grid; grid-template-columns: 38px 1fr; gap: 8px; }
.color-control input[type="color"] { width: 38px; height: 31px; padding: 2px; background: #fff; border: 1px solid #d6dae2; border-radius: 6px; }
.variable-group + .variable-group { margin-top: 14px; }
.variable-group-label { display: block; margin-bottom: 6px; color: #8a91a2; font-size: 0.66rem; font-weight: 800; letter-spacing: 0.08em; text-transform: uppercase; }
.variable-button { display: flex; align-items: center; justify-content: space-between; width: 100%; margin-top: 5px; padding: 7px 9px; color: #4d5668; font-size: 0.76rem; background: #f8f9fc; border: 1px solid #e5e8ef; border-radius: 8px; }
.variable-button:hover { color: #334155; background: #f1f1f1; border-color: #bdbdbd; }

.preview-workspace { padding: 16px; }
.editor-column > .preview-workspace { padding: 0; }
.contract-preview-canvas { max-height: 68vh; }
.contract-preview-page::v-deep .contract-document-sections { display: flex; flex: 1 1 auto; flex-direction: column; min-width: 0; }
.contract-preview-page::v-deep .contract-section-header { padding-bottom: 22px; border-bottom: 1px solid #d7dce5; }
.contract-preview-page::v-deep .contract-section-body { flex: 1 1 auto; padding: 28px 0 34px; }
.contract-preview-page::v-deep .contract-section-footer { margin-top: auto; padding-top: 22px; border-top: 1px solid #d7dce5; }
.contract-preview-page::v-deep .contract-section-empty { display: none; }
.contract-preview-page::v-deep .contract-block > :first-child { margin-top: 0; }
.contract-preview-page::v-deep .contract-block > :last-child { margin-bottom: 0; }
.contract-preview-page::v-deep .unresolved-variable { display: inline-block; padding: 1px 5px; color: #8a6500; font-size: 0.85em; background: #fff3cd; border: 1px solid #ffe69c; border-radius: 4px; }

.developer-workspace { min-height: 620px; padding: 16px; background: #111827; }
.developer-header { justify-content: space-between; gap: 16px; padding: 12px 14px; color: #f8fafc; background: #1f2937; border: 1px solid #334155; border-radius: 10px 10px 0 0; }
.developer-header small { color: #aeb8c8; }
.developer-actions { display: flex; gap: 6px; }
.developer-actions .btn { display: inline-flex; align-items: center; justify-content: center; width: 32px; height: 30px; padding: 0; }
.developer-editor-simple { min-height: calc(100vh - 330px); border: 1px solid #334155; border-top: 0; border-radius: 0 0 10px 10px; }
.developer-editor-grid { display: grid; grid-template-columns: minmax(0, 1fr) 270px; min-height: 540px; border: 1px solid #334155; border-top: 0; }
.json-editor { width: 100%; min-height: 540px; padding: 18px; color: #d8e4f3; font: 13px/1.65 SFMono-Regular, Consolas, "Liberation Mono", monospace; tab-size: 2; resize: vertical; outline: 0; background: #0f172a; border: 0; }
.schema-help { padding: 18px; color: #c2ccda; font-size: 0.75rem; background: #172033; border-left: 1px solid #334155; }
.schema-help h6 { color: #fff; }
.schema-help p { color: #9ca9ba; }
.schema-help code { display: block; margin-top: 12px; color: #93c5fd; }
.schema-help span { color: #aeb8c8; }
.schema-tip { display: flex; gap: 8px; margin-top: 22px; padding: 10px; color: #a7f3d0; background: rgba(16, 185, 129, 0.09); border: 1px solid rgba(16, 185, 129, 0.25); border-radius: 8px; }
.json-error { display: flex; gap: 8px; padding: 9px 12px; color: #fecaca; font-size: 0.78rem; background: #7f1d1d; border-right: 1px solid #991b1b; border-left: 1px solid #991b1b; }

@media (max-width: 1100px) {
  .builder-workspace { grid-template-columns: 1fr; }
  .builder-sidebar { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); max-height: none; overflow: visible; }
}
@media (max-width: 767.98px) {
  .builder-command-bar { align-items: stretch; flex-direction: column; gap: 10px; }
  .view-mode-switcher { width: 100%; }
  .view-mode-button { flex: 1 1 0; min-width: 0; }
  .developer-header { align-items: flex-start; flex-direction: column; }
  .builder-sidebar { grid-template-columns: 1fr; }
  .unified-document-canvas, .contract-preview-canvas { padding: 8px; }
  .unified-document-page, .contract-preview-page { width: 100%; }
  .developer-editor-grid { grid-template-columns: 1fr; }
  .schema-help { border-top: 1px solid #334155; border-left: 0; }
}
@media (hover: none) {
  .section-add-control {
    visibility: visible;
    opacity: 1;
    pointer-events: auto;
  }
}
</style>
